import { useState, useEffect, useRef } from 'react';
import {
  Sparkles,
  Zap,
  User,
  LogOut,
  HelpCircle,
  LayoutDashboard,
  FolderHeart,
  Images,
  CircleUserRound,
  GitBranch,
  Code,
  Menu,
  X,
  Grid2X2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CheckInButton from '../components/CheckInButton';
import { usePlatformSession } from './session';
import { useSiteName } from './useSiteName';

interface NavbarProps {
  /** 可选覆盖站点名；缺省时内部经 useSiteName 拉取 */
  siteName?: string;
  /** 兑换积分入口（页面差异：首页开 RedeemModal，其余页跳用户中心） */
  onRedeemCredits: () => void;
  /** 打开登录弹窗（AuthModal 是页面级组件） */
  onLogin: () => void;
  /** 可选覆盖退出行为；缺省用会话 logout（首页需额外清本页生成结果） */
  onLogout?: () => void;
}

export function Navbar({
  siteName: siteNameOverride,
  onRedeemCredits,
  onLogin,
  onLogout,
}: NavbarProps) {
  const {
    user,
    credits,
    isLoggedIn,
    isAdmin,
    isImpersonating,
    refreshUser,
    logout: sessionLogout,
  } = usePlatformSession();
  const fallbackSiteName = useSiteName();
  const siteName = siteNameOverride ?? fallbackSiteName;
  const userName = user?.name;
  const logout = onLogout ?? sessionLogout;
  const handleCheckIn = () => {
    void refreshUser();
  };

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileMenu]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setShowMobileMenu(false);
    setShowUserMenu(false);
  };

  return (
    <>
      <nav
        className="bg-white/80 backdrop-blur-md border-b border-light-border shadow-sm"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              type="button"
              onClick={() => handleNavigate('/')}
              className="flex items-center gap-2 shrink-0 border-0 bg-transparent p-0 text-left"
              aria-label="返回首页"
            >
              <div className="w-8 h-8 bg-accent-blue rounded-lg flex items-center justify-center shadow-md">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-light-text hidden sm:block">{siteName}</span>
              <span className="px-2 py-0.5 bg-light-hover border border-light-border rounded-lg text-xs text-light-muted hidden sm:block">
                AI生图
              </span>
            </button>

            <div className="hidden lg:flex items-center gap-2.5">
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/70 text-light-text">
                <Zap className="w-3.5 h-3.5 text-accent-blue" />
                <span className="text-sm font-medium whitespace-nowrap">{credits} 积分</span>
              </div>

              <button onClick={onRedeemCredits} className="btn-primary text-sm shadow-md">
                兑换积分
              </button>

              <button
                onClick={() => (isLoggedIn ? navigate('/llm/tokens') : onLogin())}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                <Zap className="w-3.5 h-3.5 text-accent-blue" />
                LLM Token
              </button>

              <button
                onClick={() => navigate('/gallery')}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                <Images className="w-4 h-4" />
                作品广场
              </button>

              {/* 模版广场暂时隐藏 */}
              {/* <button
                onClick={() => navigate('/templates')}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                <LayoutTemplate className="w-4 h-4" />
                模版广场
              </button> */}

              <button
                onClick={() => handleNavigate('/apps')}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                <Grid2X2 className="w-4 h-4" />
                应用中心
              </button>

              {isLoggedIn && (
                <CheckInButton compact onCheckIn={handleCheckIn} />
              )}

              {isLoggedIn ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => {
                      setShowUserMenu(!showUserMenu);
                    }}
                    className="btn-secondary p-2 flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-sm">{userName || '用户'}</span>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-light-border rounded-xl shadow-xl py-1">
                      <button
                        onClick={() => { navigate('/user'); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-light-hover flex items-center gap-2 text-light-text"
                      >
                        <CircleUserRound className="w-4 h-4" />
                        用户中心
                      </button>
                      <button
                        onClick={() => { navigate('/works'); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-light-hover flex items-center gap-2 text-light-text"
                      >
                        <FolderHeart className="w-4 h-4" />
                        我的作品库
                      </button>
                      <button
                        onClick={() => { navigate('/api-docs'); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-light-hover flex items-center gap-2 text-light-text"
                      >
                        <Code className="w-4 h-4" />
                        API 文档
                      </button>
                      <button
                        onClick={() => { navigate('/iterations'); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-light-hover flex items-center gap-2 text-light-text"
                      >
                        <GitBranch className="w-4 h-4" />
                        迭代记录
                      </button>
                      <button
                        onClick={() => { navigate('/faq'); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-light-hover flex items-center gap-2 text-light-text"
                      >
                        <HelpCircle className="w-4 h-4" />
                        帮助
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => { navigate('/admin'); setShowUserMenu(false); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-light-hover flex items-center gap-2 text-light-text"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          管理后台
                        </button>
                      )}
                      <button
                        onClick={() => { logout(); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-light-hover flex items-center gap-2 text-light-text"
                      >
                        <LogOut className="w-4 h-4" />
                        {isImpersonating ? '退出代登录' : '退出'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={onLogin} className="btn-secondary text-sm">
                  登录
                </button>
              )}
            </div>

            <div className="flex lg:hidden items-center gap-2">
              {isLoggedIn ? (
                <button
                  onClick={onRedeemCredits}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-light-hover border border-light-border rounded-lg active:bg-light-border transition-colors"
                >
                  <Zap className="w-3.5 h-3.5 text-accent-blue" />
                  <span className="text-sm font-medium text-light-text">{credits}</span>
                </button>
              ) : (
                <button
                  onClick={onLogin}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-blue text-white rounded-lg active:bg-blue-600 transition-colors text-sm font-medium"
                >
                  登录
                </button>
              )}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 rounded-xl border border-light-border hover:bg-light-hover transition-colors"
                aria-label="菜单"
              >
                {showMobileMenu ? (
                  <X className="w-5 h-5 text-light-text" />
                ) : (
                  <Menu className="w-5 h-5 text-light-text" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {showMobileMenu && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] lg:hidden"
          style={{ top: '64px' }}
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      <div
        className={`fixed right-0 z-[9998] w-72 max-w-[85vw] bg-white border-l border-light-border shadow-2xl transform transition-transform duration-300 ease-out lg:hidden ${
          showMobileMenu ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: '64px', bottom: 0 }}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          <div className="p-4 space-y-2">
            <div className="flex items-center justify-between p-3 bg-light-hover rounded-xl">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-accent-blue" />
                <span className="text-sm font-medium text-light-text">{credits} 积分</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowMobileMenu(false); isLoggedIn ? navigate('/llm/tokens') : onLogin(); }}
                  className="btn-secondary text-xs px-3 py-1.5"
                >
                  LLM Token
                </button>
                <button onClick={() => { setShowMobileMenu(false); onRedeemCredits(); }} className="btn-primary text-xs px-3 py-1.5">
                  兑换
                </button>
              </div>
            </div>

            <div className="space-y-1 pt-2">
              <p className="px-3 text-xs font-medium text-light-muted uppercase tracking-wider">导航</p>
              <button
                onClick={() => handleNavigate('/gallery')}
                className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
              >
                <Images className="w-4 h-4 text-accent-blue" />
                作品广场
              </button>
              {/* 模版广场暂时隐藏 */}
              {/* <button
                onClick={() => handleNavigate('/templates')}
                className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
              >
                <LayoutTemplate className="w-4 h-4 text-accent-blue" />
                模版广场
              </button> */}
              <button
                onClick={() => handleNavigate('/apps')}
                className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
              >
                <Grid2X2 className="w-4 h-4 text-accent-blue" />
                应用中心
              </button>
            </div>

            {isLoggedIn && (
              <>
                <div className="border-t border-light-border my-2" />
                <div className="space-y-1">
                  <p className="px-3 text-xs font-medium text-light-muted uppercase tracking-wider">我的</p>
                  <div className="px-3 py-1">
                    <CheckInButton compact onCheckIn={handleCheckIn} />
                  </div>
                  <button
                    onClick={() => handleNavigate('/user')}
                    className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
                  >
                    <CircleUserRound className="w-4 h-4 text-accent-blue" />
                    用户中心
                  </button>
                  <button
                    onClick={() => handleNavigate('/works')}
                    className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
                  >
                    <FolderHeart className="w-4 h-4 text-accent-blue" />
                    我的作品库
                  </button>
                  <button
                    onClick={() => handleNavigate('/iterations')}
                    className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
                  >
                    <GitBranch className="w-4 h-4 text-accent-blue" />
                    迭代记录
                  </button>
                  <button
                    onClick={() => handleNavigate('/api-docs')}
                    className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
                  >
                    <Code className="w-4 h-4 text-accent-blue" />
                    API 文档
                  </button>
                </div>
              </>
            )}

            <div className="border-t border-light-border my-2" />
            <div className="space-y-1">
              <button
                onClick={() => handleNavigate('/faq')}
                className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
              >
                <HelpCircle className="w-4 h-4 text-accent-blue" />
                帮助
              </button>
              {isAdmin && (
                <button
                  onClick={() => handleNavigate('/admin')}
                  className="w-full px-3 py-2.5 text-left text-sm rounded-xl hover:bg-light-hover flex items-center gap-3 text-light-text transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4 text-accent-blue" />
                  管理后台
                </button>
              )}
            </div>
          </div>

          <div className="mt-auto p-4 border-t border-light-border space-y-2">
            {isLoggedIn ? (
              <button
                onClick={() => { logout(); setShowMobileMenu(false); }}
                className="w-full px-4 py-2.5 text-left text-sm rounded-xl hover:bg-red-50 flex items-center gap-3 text-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {isImpersonating ? '退出代登录' : '退出登录'}
              </button>
            ) : (
              <button onClick={onLogin} className="w-full btn-primary text-sm">
                登录
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
